/* eslint-disable functional/immutable-data */

import type { Cache } from '@urql/exchange-graphcache';

import type {
  BillingAccount,
  Customer,
  DeleteBillingAccountInput,
  DeleteBillingAccountMutation,
  SubmitBraintreeBillingAccountInput,
  SubmitBraintreeBillingAccountMutation,
  SubmitBraintreeBillingAccountSuccessResponse,
  UpdateBillingAccountInput,
  UpdateBillingAccountMutation,
} from '@order/graphql';
import { CurrentCustomerDocument } from '@order/graphql';

import { updateCacheWithPaymentMethodCardChanges } from './paymentMethodCards';

/**
 * Changing billing accounts returns a billing account object.
 * This is fine for when the billing account is updated,
 * But if it is added or removed, this is needed to update
 * the current customer's object's billing accounts in URQL cache.
 */

export const submitBraintreeBillingAccount = (
  result: SubmitBraintreeBillingAccountMutation,
  _args: SubmitBraintreeBillingAccountInput,
  cache: Cache,
) => {
  const response = result.submitBraintreeBillingAccount;
  const data = response as SubmitBraintreeBillingAccountSuccessResponse;
  const typename = response.__typename;

  // Ignore unsuccessful mutations.
  if (typename !== 'SubmitBraintreeBillingAccountSuccessResponse') return;

  updateCacheWithBillingAccountChanges(cache, data);
  updateCacheWithPaymentMethodCardChanges(cache, {
    paymentMethod: {
      ...data.billingAccount,
      __typename: 'PaymentMethodBillingAccount',
    },
  });
};

export const updateBillingAccount = (
  result: UpdateBillingAccountMutation,
  _args: UpdateBillingAccountInput,
  cache: Cache,
) => {
  const response = result.updateBillingAccount;
  const { billingAccount } = response;

  // Ignore unsuccessful mutations.
  if (!billingAccount?.id) return;

  updateCacheWithBillingAccountChanges(cache, response);
  updateCacheWithPaymentMethodCardChanges(cache, {
    paymentMethod: {
      ...billingAccount,
      __typename: 'PaymentMethodBillingAccount',
    },
  });
};

export const deleteBillingAccount = (
  result: DeleteBillingAccountMutation,
  args: Readonly<{ input: DeleteBillingAccountInput }>,
  cache: Cache,
) => {
  const deletedId = args?.input.id;

  // Ignore unsuccessful mutations.
  if (!deletedId || !result?.deleteBillingAccount?.success) return;

  updateCacheWithBillingAccountChanges(cache, {
    billingAccount: { id: String(deletedId) },
    removed: true,
  });
  updateCacheWithPaymentMethodCardChanges(cache, {
    paymentMethod: {
      id: String(deletedId),
      __typename: 'PaymentMethodBillingAccount',
    },
    removed: true,
  });
};

const updateCacheWithBillingAccountChanges = (
  customerCache: Cache,
  billingAccountChanges: BillingAccountChanges,
) => {
  customerCache.updateQuery({ query: CurrentCustomerDocument }, (data) => {
    if (!data) return data;

    // Get cached billing accounts.
    const cachedCustomer = data?.currentCustomer as Customer;
    const billingAccounts = cachedCustomer?.billingAccounts ?? [];
    const withoutUpdated = billingAccounts.filter(
      ({ id }) => id !== billingAccountChanges.billingAccount.id,
    );

    // Get stale billing account.
    const stalePaymentMethod = billingAccounts.find(
      ({ id }) => id === billingAccountChanges.billingAccount.id,
    );

    // If the billing account being removed was default, set the first other as default.
    if (billingAccountChanges.removed && stalePaymentMethod?.isDefault) {
      data.currentCustomer = {
        ...cachedCustomer,
        billingAccounts: withoutUpdated.map((billingAccount, index) => {
          if (index === 0) return { ...billingAccount, isDefault: true };

          return billingAccount;
        }),
      };

      return data;
    }

    // If the billing account was removed, remove it from the cache.
    if (billingAccountChanges.removed) {
      data.currentCustomer = {
        ...cachedCustomer,
        billingAccounts: withoutUpdated,
      };

      return data;
    }

    // If the billing account was changed to default, remove other defaults from the list.
    if (billingAccountChanges.billingAccount?.isDefault) {
      data.currentCustomer = {
        ...cachedCustomer,
        billingAccounts: [
          ...withoutUpdated.map((billingAccount) => ({
            ...billingAccount,
            isDefault: false,
          })),
          billingAccountChanges.billingAccount as BillingAccount,
        ].sort((c1, c2) => c1.id.localeCompare(c2.id)),
      };

      return data;
    }

    // If the billing account was added, add it and sort by id.
    data.currentCustomer = {
      ...cachedCustomer,
      billingAccounts: [
        ...withoutUpdated,
        billingAccountChanges.billingAccount as BillingAccount,
      ].sort((c1, c2) => c1.id.localeCompare(c2.id)),
    };

    return data;
  });
};

type BillingAccountChanges = Readonly<{
  billingAccount: Partial<BillingAccount>;
  removed?: boolean;
}>;
